import { useState } from 'react';

/* ─── Rigging Styles data ─── */
const riggingStyleTabs = [
  {
    id: 'single',
    label: 'Single Configurations',
    items: [
      {
        id: 'canopy',
        name: 'One-Side Down',
        subtitle: 'One-Side Down',
        description:
          'A dynamic asymmetric configuration where one side of the tent is anchored low to the ground while the opposite side is raised high. Ideal for creating dramatic, directional shelter with a striking visual profile.',
        detail:
          'The one-side-down configuration is perfect when you need shelter on one side while keeping the other open and elevated. The low anchored edge provides wind protection and privacy, while the raised side allows natural light, airflow, and easy access — making it a popular choice for outdoor dining, bar setups, and entrance canopies.',
        tags: ['Outdoor Dining', 'Bar Setups', 'Entrance Canopies', 'Asymmetric'],
        image: '/assets/img/c277253f4ae7.webp',
      },
      {
        id: 'three-sides',
        name: 'Three-Sides Down',
        subtitle: 'Three-Sides Down',
        description:
          'Great option for stages and pop-up retail spaces. With three sides anchored down and one side open, this configuration provides shelter and definition while maintaining excellent flow and visibility.',
        detail:
          'The three-sides-down configuration creates a natural entrance or stage backdrop. One open face allows for easy access and sightlines, making it ideal for performances, retail activations, and hospitality setups where flow and visibility matter.',
        tags: ['Stages', 'Pop-Up Retail', 'Open Front', 'Hospitality'],
        image: '/assets/img/ac5fa473b1b4.webp',
      },
      {
        id: 'off-structure',
        name: 'Rigged Off-Structure',
        subtitle: 'Rigged Off-Structure',
        description:
          'Can be rigged off and around existing architectural structures, trees, or buildings. This configuration adapts to the natural environment, blending seamlessly with the surrounding space.',
        detail:
          'When poles are not practical, our tents can be tensioned directly off existing structures, such as walls, trees, pergolas, or buildings. This creates a seamless integration with the environment and opens up rigging possibilities in constrained or architecturally interesting spaces.',
        tags: ['Architecture', 'Trees', 'Buildings', 'Adaptive'],
        image: '/assets/img/01c9ea51bb69.webp',
      },
    ],
  },
  {
    id: 'joined',
    label: 'Joined Tents',
    items: [
      {
        id: 'joined-modular',
        name: 'Joined Stretch Tents',
        subtitle: 'Modular Joined System',
        description:
          'Join multiple modular stretch tents together to create a larger event space. Ideal for festivals and large corporate events, the joining system allows for seamless coverage across vast areas without compromising on style or structural integrity.',
        detail:
          'Our patented joining hardware clips tent edges together, creating a weatherproof seal between modules. Add as many modules as needed, scaling from intimate to massive. Mix tent sizes and shapes to create a totally custom layout suited to any venue footprint.',
        tags: ['Festivals', 'Corporate Events', 'Large Gatherings', 'Seamless Coverage'],
        image: '/assets/img/2bafd89af33d.webp',
      },
      {
        id: 'multi-zone',
        name: 'Multi-Zone Events',
        subtitle: 'Multi-Zone Layouts',
        description:
          'Define distinct zones for registration, dining, and entertainment using connected tent modules while maintaining a flowing, cohesive event aesthetic throughout.',
        detail:
          'By connecting multiple tent modules at different angles and heights, you can create clearly defined zones within a single seamless structure. This is ideal for events that require separate areas for different activities while maintaining a unified visual identity.',
        tags: ['Multi-Zone', 'Dining', 'Entertainment', 'Registration'],
        image: '/assets/img/2d9020fd7b11.webp',
      },
      {
        id: 'extended-cover',
        name: 'Extended Venue Cover',
        subtitle: 'Extended Coverage',
        description:
          'When a single tent cannot span the required area, extend coverage seamlessly. Perfect for unconventional sites, like terraces or steps.',
        detail:
          'Extended coverage configurations allow you to cover long, narrow, or irregularly shaped venues that a single tent cannot accommodate. The joining system maintains structural integrity and weatherproofing across the entire connected span.',
        tags: ['Manor Gardens', 'Racetracks', 'Open Fields', 'Large Venues'],
        image: '/assets/img/festival-vendor-village-12.webp',
      },
    ],
  },
];

/* ─── Component ─── */
export default function RiggingInfo() {
  const [activeStyleTab, setActiveStyleTab] = useState('single');
  const [activeItemId, setActiveItemId] = useState('canopy');

  const activeTab = riggingStyleTabs.find((t) => t.id === activeStyleTab) ?? riggingStyleTabs[0];
  const activeItem = activeTab.items.find((i) => i.id === activeItemId) ?? activeTab.items[0];

  const handleTabChange = (tabId: string) => {
    const tab = riggingStyleTabs.find((t) => t.id === tabId);
    setActiveStyleTab(tabId);
    if (tab) setActiveItemId(tab.items[0].id);
  };

  return (
    <>
      {/* ── Rigging Styles ── */}
      <section id="rigging-styles" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="mb-10 text-center">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">
              One Tent. Endless Configurations.
            </h2>
            <p className="text-gray-500 text-base max-w-2xl leading-relaxed mx-auto">
              The unique two-way stretch of our tent fabric allows for a remarkable range of rigging configurations. Whether you need open-air canopies, enclosed shelters, or fully freeform structures, the same tent adapts to suit any venue, environment, or occasion.
            </p>
          </div>

          {/* Top-level style tabs */}
          <div className="flex gap-3 mb-8 flex-wrap justify-center">
            {riggingStyleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  activeStyleTab === tab.id
                    ? 'bg-teal-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Sub-item pills */}
          <div className="flex gap-2 mb-10 flex-wrap justify-center">
            {activeTab.items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveItemId(item.id)}
                className={`px-5 py-2 rounded-full text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  activeItemId === item.id
                    ? 'bg-teal-500 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-teal-300 hover:text-teal-600'
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>

          {/* Content - image LEFT, text RIGHT */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
            <div className="w-full h-[420px] rounded-xl overflow-hidden">
              <img
                key={activeItem.id}
                src={activeItem.image}
                alt={activeItem.name}
                className="w-full h-full object-cover object-top animate-[fadeIn_0.4s_ease-in-out]"
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">{activeItem.subtitle}</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">{activeItem.description}</p>
              <p className="text-gray-500 text-sm leading-relaxed mb-7">{activeItem.detail}</p>

            </div>
          </div>
        </div>
      </section>

      {/* ── Joining System ── */}
      <section id="joining-system" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-start gap-0">
            {[
              { icon: 'ri-search-eye-line', step: '01', title: 'Choose Your Tents', desc: 'Select the size and number of stretch tent modules that best match your event footprint and layout requirements.' },
              { icon: 'ri-links-line', step: '02', title: 'Connect with Joining System', desc: 'Our patented joining hardware clips the tent edges together, creating a weatherproof seal between modules.' },
              { icon: 'ri-building-4-line', step: '03', title: 'Create Your Space', desc: 'Raise the masts, tension the fabric and stand back. Your seamlessly joined event space is ready to use.' },
            ].map((s, i, arr) => (
              <div key={i} className="flex flex-col lg:flex-row items-start flex-1">
                <div className="flex-1 flex flex-col items-center text-center px-6">
                  <div className="w-20 h-20 bg-teal-400 rounded-2xl flex items-center justify-center mb-5">
                    <i className={`${s.icon} text-3xl text-white`}></i>
                  </div>
                  <span className="text-xs font-bold tracking-widest text-teal-400 uppercase mb-2">Step {s.step}</span>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed max-w-xs">{s.desc}</p>
                </div>
                {i < arr.length - 1 && (
                  <div className="hidden lg:flex items-center justify-center w-12 mt-8 flex-shrink-0">
                    <i className="ri-arrow-right-line text-3xl text-teal-300"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
